// AppShell — wraps the entire app in the .stumble-app div, providing palette + grain + bg-blobs.
// Sets data-palette attribute for runtime palette switching (poster / midnight / faded).
// Per ADR-0002 default = "poster".
//
// Also publishes --vv-bottom-chrome (px) to the document root by subscribing to
// visualViewport — see useVisualViewport.ts. Fixed-positioned elements use it
// in stumble-overrides.css to pad above iOS Safari's URL bar + soft keyboard.

import { type ReactNode, useEffect, useState } from 'react'
import { useVisualViewport } from '../lib/useVisualViewport'

export type Palette = 'poster' | 'midnight' | 'faded'

const PALETTE_KEY = 'stumble.palette'

// Canon phone size — iPhone 13 (390×844). All canon px values + screens were
// designed against this canvas. Below the breakpoint we let the viewport drive
// dimensions (real phones). Above it we lock the canvas + scale it.
const PHONE_W = 390
const PHONE_H = 844
const DESKTOP_BREAKPOINT = 481

export function AppShell({ children }: { children: ReactNode }) {
  const [palette, setPalette] = useState<Palette>('poster')
  const [layout, setLayout] = useState<{ mode: 'phone' | 'desktop'; scale: number }>({
    mode: 'phone',
    scale: 1,
  })
  const vv = useVisualViewport()

  // Publish the live iOS-Safari bottom-chrome gap as a CSS variable on the
  // root element. Consumers (.bottom-nav, .sheet, sticky action bars) use
  // max(env(safe-area-inset-bottom), var(--vv-bottom-chrome)) to stay above
  // both the home indicator AND the URL bar / soft keyboard.
  //
  // Desktop-scaled layout doesn't need this (no Safari chrome on desktop),
  // but we publish it anyway — the value will be 0 there.
  useEffect(() => {
    if (typeof document === 'undefined') return
    document.documentElement.style.setProperty('--vv-bottom-chrome', `${vv.bottomChrome}px`)
  }, [vv.bottomChrome])

  // Load stored palette + write changes back.
  useEffect(() => {
    try {
      const stored = localStorage.getItem(PALETTE_KEY) as Palette | null
      if (stored === 'poster' || stored === 'midnight' || stored === 'faded') {
        setPalette(stored)
      }
    } catch {
      // ignore — privacy-mode etc.
    }
  }, [])

  useEffect(() => {
    try {
      localStorage.setItem(PALETTE_KEY, palette)
    } catch {
      // ignore
    }
  }, [palette])

  // Responsive layout: on phones the app is edge-to-edge. On desktop/tablet
  // we render the canon 390×844 canvas at uniform scale to fit the viewport,
  // letterboxed in a dark frame. transform:scale keeps the design pixel-perfect
  // at any monitor size; click/touch coordinates map through the transform.
  useEffect(() => {
    function recompute() {
      const vw = window.innerWidth
      const vh = window.innerHeight
      if (vw < DESKTOP_BREAKPOINT) {
        setLayout({ mode: 'phone', scale: 1 })
        return
      }
      // Leave ~24px breathing room on each axis so the phone doesn't touch
      // the viewport edges on small laptops.
      const pad = 24
      const scale = Math.min((vw - pad * 2) / PHONE_W, (vh - pad * 2) / PHONE_H)
      setLayout({ mode: 'desktop', scale: Math.max(0.5, Math.min(scale, 2)) })
    }
    recompute()
    window.addEventListener('resize', recompute)
    return () => window.removeEventListener('resize', recompute)
  }, [])

  if (layout.mode === 'phone') {
    return (
      <div
        className="stumble-app"
        data-palette={palette}
        style={{
          position: 'relative',
          width: '100%',
          // D4: use 100lvh (large viewport) so the .stumble-app wrapper extends
          // BEHIND iOS Safari's URL bar + status area. Grain + bg-blobs paint
          // there, producing the edge-to-edge yellow continuity users asked for.
          // Layout-driving min-height stays 100dvh elsewhere so content sized
          // off dvh still fits the visible area.
          minHeight: '100lvh',
          overflow: 'hidden',
        }}
      >
        {/* canon drifting blob background — drift 18s keyframe (canon.css) */}
        <div className="bg-blobs" aria-hidden />
        {children}
        <div className="grain" aria-hidden />
      </div>
    )
  }

  // Desktop / tablet — phone canvas scaled uniformly inside a centered frame.
  const scaledW = PHONE_W * layout.scale
  const scaledH = PHONE_H * layout.scale
  return (
    <div
      style={{
        minHeight: '100dvh',
        background: '#15131c',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: scaledW,
          height: scaledH,
          position: 'relative',
          borderRadius: 36 * layout.scale,
          overflow: 'hidden',
          boxShadow: '0 40px 80px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.04)',
        }}
      >
        <div
          className="stumble-app"
          data-palette={palette}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: PHONE_W,
            height: PHONE_H,
            overflow: 'hidden',
            transform: `scale(${layout.scale})`,
            transformOrigin: 'top left',
          }}
        >
          {/* canon drifting blob background — drift 18s keyframe (canon.css) */}
          <div className="bg-blobs" aria-hidden />
          {children}
          <div className="grain" aria-hidden />
        </div>
      </div>
    </div>
  )
}
